<template>
    <div class="web-tab-detail-page">
        <AppBreadCrumb
            :paths="[
                { title: $t('webTabsPage.breadcrumbTitle'), path: '/integrations/webtabs' },
                { title: isEditMode ? $t('webTabsPage.breadcrumbEdit') : $t('webTabsPage.breadcrumbNew') },
            ]"
        />

        <div class="form-container">
            <div class="form-header">
                <div class="form-header-icon">
                    <span class="material-icons" :style="{ color: formData.iconColor || '#6366f1' }">
                        {{ formData.icon || "tab" }}
                    </span>
                </div>
                <div class="form-header-text">
                    <h1>{{ isEditMode ? $t("webTabsPage.formTitleEdit") : $t("webTabsPage.formTitleNew") }}</h1>
                    <p>{{ $t("webTabsPage.formSubtitle") }}</p>
                </div>
            </div>

            <a-spin :spinning="loading">
                <a-form :form="form" @submit.prevent="handleSubmit" class="webtab-form">
                    <!-- STEP 1: Yerleşim Yeri -->
                    <div class="form-section">
                        <div class="section-header">
                            <span class="section-number">1</span>
                            <div class="section-title">
                                <h3>{{ $t("webTabsPage.sectionPlacementTitle") }}</h3>
                                <p>{{ $t("webTabsPage.sectionPlacementDescription") }}</p>
                            </div>
                        </div>
                        <div class="placement-grid">
                            <a-popover
                                v-for="option in placementOptions"
                                :key="option.value"
                                placement="right"
                                trigger="hover"
                                :overlayStyle="{ maxWidth: '400px' }"
                            >
                                <template #content>
                                    <div class="placement-popover">
                                        <img :src="option.previewImage" :alt="option.title" class="popover-image" />
                                        <div class="popover-title">{{ option.title }}</div>
                                        <div class="popover-desc">{{ option.longDescription }}</div>
                                    </div>
                                </template>
                                <div
                                    class="placement-card"
                                    :class="{ selected: formData.placement === option.value }"
                                    @click="formData.placement = option.value"
                                >
                                    <div class="placement-card-icon" :style="{ background: option.color }">
                                        <span class="material-icons">{{ option.icon }}</span>
                                    </div>
                                    <div class="placement-card-content">
                                        <div class="placement-card-title">{{ option.title }}</div>
                                        <div class="placement-card-desc">{{ option.description }}</div>
                                    </div>
                                    <div class="placement-card-check" v-if="formData.placement === option.value">
                                        <span class="material-icons">check_circle</span>
                                    </div>
                                </div>
                            </a-popover>
                        </div>

                        <!-- Toolbar Ek Ayarları -->
                        <div v-if="formData.placement === 'toolbar'" class="sub-options">
                            <div class="sub-option-row">
                                <label>Açılış Modu</label>
                                <a-radio-group v-model="formData.toolbarMode" button-style="solid" size="small">
                                    <a-radio-button value="popup">Popup</a-radio-button>
                                    <a-radio-button value="parent">Sayfa</a-radio-button>
                                </a-radio-group>
                            </div>
                            <div v-if="formData.toolbarMode === 'popup'" class="sub-option-row">
                                <label>
                                    <span>Sürekli Açık Mod</span>
                                    <span class="label-hint">(arka planda çalışmaya devam eder)</span>
                                </label>
                                <a-switch v-model="formData.keepAlive" size="small" />
                            </div>
                        </div>

                        <!-- Contact bilgi notu -->
                        <div v-if="isContactPlacement" class="info-badge">
                            <span class="material-icons">info</span>
                            <span>Bu yerleşim için kişi bilgilerini URL parametresi olarak kullanabilirsiniz</span>
                        </div>

                        <!-- Tablo Kayıt Sekmesi Ek Ayarları -->
                        <div v-if="isTableRecordPlacement" class="sub-options">
                            <div class="sub-option-row">
                                <label>Kapsam</label>
                                <a-radio-group v-model="formData.tableRecordConfig.scope" button-style="solid" size="small">
                                    <a-radio-button value="all">Tüm Tablolar</a-radio-button>
                                    <a-radio-button value="specific">Belirli Tablo</a-radio-button>
                                </a-radio-group>
                            </div>
                            <div v-if="formData.tableRecordConfig.scope === 'specific'" class="sub-option-row">
                                <label>Tablo Seçin</label>
                                <a-select
                                    v-model="formData.tableRecordConfig.tableId"
                                    placeholder="Tablo seçin..."
                                    show-search
                                    size="small"
                                    style="width: 200px"
                                    :filter-option="filterTable"
                                >
                                    <a-select-option v-for="table in tables" :key="table._id" :value="table._id">
                                        {{ table.name }}
                                    </a-select-option>
                                </a-select>
                            </div>
                        </div>
                        <div v-if="isTableRecordPlacement" class="info-badge">
                            <span class="material-icons">info</span>
                            <span>Bu yerleşim için tablo ve kayıt bilgilerini URL parametresi olarak kullanabilirsiniz</span>
                        </div>

                        <!-- Görev Detay Sekmesi bilgi notu -->
                        <div v-if="isTaskDetailPlacement" class="info-badge">
                            <span class="material-icons">info</span>
                            <span>Bu yerleşim için görev bilgilerini URL parametresi olarak kullanabilirsiniz</span>
                        </div>
                    </div>

                    <!-- STEP 2: Kaynak Türü -->
                    <div class="form-section">
                        <div class="section-header">
                            <span class="section-number">2</span>
                            <div class="section-title">
                                <h3>Kaynak Türü</h3>
                                <p>İçeriğin nereden geleceğini belirleyin</p>
                            </div>
                        </div>
                        <div class="type-options">
                            <div
                                class="type-card"
                                :class="{ selected: formData.type === 'url' }"
                                @click="formData.type = 'url'"
                            >
                                <div class="type-card-header">
                                    <div class="type-card-icon url-icon">
                                        <span class="material-icons">link</span>
                                    </div>
                                    <div class="type-card-check" v-if="formData.type === 'url'">
                                        <span class="material-icons">check_circle</span>
                                    </div>
                                </div>
                                <h4>Harici URL</h4>
                                <p>
                                    Dışarıdaki herhangi bir web sayfasını embed edin. CRM, dashboard, dokümantasyon veya
                                    herhangi bir web uygulaması olabilir.
                                </p>
                                <ul class="type-features">
                                    <li><span class="material-icons">check</span> Dinamik parametreler ekleyin</li>
                                    <li><span class="material-icons">check</span> Kullanıcı/site bilgilerini iletin</li>
                                    <li><span class="material-icons">check</span> Anında entegrasyon</li>
                                </ul>
                            </div>
                            <div
                                class="type-card"
                                :class="{ selected: formData.type === 'app' }"
                                @click="formData.type = 'app'"
                            >
                                <div class="type-card-header">
                                    <div class="type-card-icon app-icon">
                                        <span class="material-icons">apps</span>
                                    </div>
                                    <div class="type-card-check" v-if="formData.type === 'app'">
                                        <span class="material-icons">check_circle</span>
                                    </div>
                                </div>
                                <h4>Web App</h4>
                                <p>
                                    Kendi frontend uygulamalarınızı Supsis'de ücretsiz host edin. ZIP dosyası yükleyin
                                    veya Supsis'de AI ile oluşturun.
                                </p>
                                <ul class="type-features">
                                    <li><span class="material-icons">check</span> Ücretsiz hosting</li>
                                    <li><span class="material-icons">check</span> React, Vue, HTML/JS desteği</li>
                                    <li><span class="material-icons">check</span> AI ile uygulama oluşturma</li>
                                </ul>
                            </div>
                        </div>

                        <!-- URL Input -->
                        <div v-if="formData.type === 'url'" class="type-config">
                            <a-input v-model="formData.url" placeholder="https://example.com/dashboard" size="large">
                                <span slot="prefix" class="material-icons" style="color: #999; font-size: 18px"
                                    >language</span
                                >
                            </a-input>
                        </div>

                        <!-- App Seçimi -->
                        <div v-if="formData.type === 'app'" class="type-config">
                            <div class="app-select-wrapper">
                                <a-select
                                    v-model="formData.appId"
                                    placeholder="Web App seçin..."
                                    show-search
                                    size="large"
                                    :filter-option="filterWebApp"
                                    style="flex: 1"
                                >
                                    <a-select-option v-for="app in availableWebApps" :key="app._id" :value="app._id">
                                        <div class="app-option">
                                            <span class="material-icons" :style="{ color: app.iconColor }">{{
                                                app.icon
                                            }}</span>
                                            <span>{{ app.name }}</span>
                                            <span class="app-slug">{{ app.slug }}.webapp.supsis.ai</span>
                                        </div>
                                    </a-select-option>
                                </a-select>
                                <a-button
                                    type="primary"
                                    ghost
                                    @click="$router.push('/integrations/webapps/new')"
                                    class="create-app-btn"
                                >
                                    <span class="material-icons">add</span>
                                    Yeni App Oluştur
                                </a-button>
                            </div>
                            <div v-if="selectedApp" class="selected-app-info">
                                <span class="material-icons">public</span>
                                <span>https://{{ selectedApp.slug }}.webapp.supsis.ai</span>
                            </div>
                        </div>
                    </div>

                    <!-- STEP 3: Görünüm -->
                    <div class="form-section">
                        <div class="section-header">
                            <span class="section-number">3</span>
                            <div class="section-title">
                                <h3>Görünüm</h3>
                                <p>İsim, ikon ve açıklama ayarlayın</p>
                            </div>
                        </div>
                        <div class="appearance-grid">
                            <div class="appearance-icon-color">
                                <div class="icon-picker-compact">
                                    <label>İkon</label>
                                    <MaterialIconPicker v-model="formData.icon" />
                                </div>
                                <div class="color-picker-compact">
                                    <label>Renk</label>
                                    <div class="color-input-row">
                                        <color-picker v-model="formData.iconColor" />
                                        <a-input
                                            v-model="formData.iconColor"
                                            placeholder="#6366f1"
                                            size="small"
                                            style="width: 90px"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="appearance-text">
                                <div class="input-group">
                                    <label>Menü Adı <span class="required">*</span></label>
                                    <a-input v-model="formData.name" placeholder="Örn: CRM Sistemi" />
                                </div>
                                <div class="input-group">
                                    <label>Açıklama <span class="optional">(tooltip)</span></label>
                                    <a-input v-model="formData.description" placeholder="Kısa bir açıklama..." />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- STEP 4: Query Parametreleri (Collapsible) -->
                    <div class="form-section collapsible" :class="{ expanded: showQueryParams }">
                        <div class="section-header clickable" @click="showQueryParams = !showQueryParams">
                            <span class="section-number">4</span>
                            <div class="section-title">
                                <h3>Query Parametreleri</h3>
                                <p>URL'ye dinamik parametreler ekleyin (opsiyonel)</p>
                            </div>
                            <span class="material-icons expand-icon">{{
                                showQueryParams ? "expand_less" : "expand_more"
                            }}</span>
                        </div>
                        <div class="section-content" v-show="showQueryParams">
                            <div class="query-params-list">
                                <div
                                    v-for="(param, index) in formData.queryParams"
                                    :key="index"
                                    class="query-param-row"
                                >
                                    <a-input v-model="param.key" placeholder="Key" size="small" style="width: 120px" />
                                    <span class="param-equals">=</span>
                                    <a-input
                                        v-model="param.value"
                                        placeholder="Value veya ${dinamik}"
                                        size="small"
                                        style="flex: 1"
                                    />
                                    <a-select
                                        v-model="param.dynamicValue"
                                        placeholder="Dinamik"
                                        size="small"
                                        style="width: 180px"
                                        @change="(val) => handleDynamicValueChange(index, val)"
                                    >
                                        <a-select-opt-group label="Manuel">
                                            <a-select-option value="">Manuel</a-select-option>
                                        </a-select-opt-group>
                                        <a-select-opt-group label="Kullanıcı">
                                            <a-select-option value="${user.name}">Adı</a-select-option>
                                            <a-select-option value="${user.email}">Email</a-select-option>
                                            <a-select-option value="${user.id}">ID</a-select-option>
                                            <a-select-option value="${user.token}">Token</a-select-option>
                                        </a-select-opt-group>
                                        <a-select-opt-group label="Site">
                                            <a-select-option value="${site.id}">Site ID</a-select-option>
                                        </a-select-opt-group>
                                        <a-select-opt-group v-if="isContactPlacement" label="Kişi">
                                            <a-select-option value="${contact.id}">ID</a-select-option>
                                            <a-select-option value="${contact.fullname}">Ad Soyad</a-select-option>
                                            <a-select-option value="${contact.email}">E-posta</a-select-option>
                                            <a-select-option value="${contact.phone}">Telefon</a-select-option>
                                            <a-select-option value="${contact.whatsappNumber}">WhatsApp</a-select-option>
                                            <a-select-option value="${contact.platform}">Platform</a-select-option>
                                        </a-select-opt-group>
                                        <a-select-opt-group v-if="isTableRecordPlacement" label="Tablo">
                                            <a-select-option value="${table.id}">Tablo ID</a-select-option>
                                            <a-select-option value="${table.name}">Tablo Adı</a-select-option>
                                        </a-select-opt-group>
                                        <a-select-opt-group v-if="isTableRecordPlacement" label="Kayıt">
                                            <a-select-option value="${record.id}">Kayıt ID</a-select-option>
                                            <a-select-option value="${record.recordId}">Kayıt Numarası</a-select-option>
                                        </a-select-opt-group>
                                        <a-select-opt-group v-if="isTableRecordPlacement && selectedTableFields.length > 0" label="Tablo Alanları">
                                            <a-select-option
                                                v-for="field in selectedTableFields"
                                                :key="field._id"
                                                :value="`\${field.${field._id}}`"
                                            >
                                                {{ field.name }}
                                            </a-select-option>
                                        </a-select-opt-group>
                                        <a-select-opt-group v-if="isTaskDetailPlacement" label="Görev">
                                            <a-select-option value="${task.id}">Görev ID</a-select-option>
                                            <a-select-option value="${task.title}">Görev Başlığı</a-select-option>
                                            <a-select-option value="${task.description}">Görev Açıklaması</a-select-option>
                                            <a-select-option value="${task.priority}">Öncelik</a-select-option>
                                            <a-select-option value="${task.startDate}">Başlangıç Tarihi</a-select-option>
                                            <a-select-option value="${task.endDate}">Bitiş Tarihi</a-select-option>
                                            <a-select-option value="${task.completed}">Tamamlandı mı</a-select-option>
                                            <a-select-option value="${task.done}">Done Durumu</a-select-option>
                                            <a-select-option value="${task.pipelineStage}">Aşama ID</a-select-option>
                                            <a-select-option value="${task.workflow}">Workflow ID</a-select-option>
                                        </a-select-opt-group>
                                        <a-select-opt-group v-if="isTaskDetailPlacement" label="Görev Sahibi">
                                            <a-select-option value="${task.owner.id}">Sahip ID</a-select-option>
                                            <a-select-option value="${task.owner.name}">Sahip Adı</a-select-option>
                                            <a-select-option value="${task.owner.email}">Sahip Email</a-select-option>
                                        </a-select-opt-group>
                                        <a-select-opt-group v-if="isTaskDetailPlacement" label="Görev Atananlar">
                                            <a-select-option value="${task.assignees}">Atananlar (ID listesi)</a-select-option>
                                            <a-select-option value="${task.assigneesNames}">Atananlar (İsim listesi)</a-select-option>
                                        </a-select-opt-group>
                                    </a-select>
                                    <a-button
                                        type="link"
                                        size="small"
                                        @click="removeQueryParam(index)"
                                        class="remove-param-btn"
                                    >
                                        <span class="material-icons">close</span>
                                    </a-button>
                                </div>
                            </div>
                            <a-button type="dashed" size="small" @click="addQueryParam" class="add-param-btn">
                                <span class="material-icons">add</span>
                                Parametre Ekle
                            </a-button>
                        </div>
                    </div>

                    <!-- STEP 5: Diğer Ayarlar (Collapsible) -->
                    <div class="form-section collapsible" :class="{ expanded: showAdvanced }">
                        <div class="section-header clickable" @click="showAdvanced = !showAdvanced">
                            <span class="section-number">5</span>
                            <div class="section-title">
                                <h3>Diğer Ayarlar</h3>
                                <p>Görünürlük, sıralama ve durum</p>
                            </div>
                            <span class="material-icons expand-icon">{{
                                showAdvanced ? "expand_less" : "expand_more"
                            }}</span>
                        </div>
                        <div class="section-content" v-show="showAdvanced">
                            <div class="advanced-grid">
                                <div class="advanced-item">
                                    <label>Sıralama</label>
                                    <a-input-number
                                        v-model="formData.order"
                                        :min="0"
                                        size="small"
                                        style="width: 80px"
                                    />
                                    <span class="hint">Düşük = önce</span>
                                </div>
                                <div class="advanced-item">
                                    <label>Durum</label>
                                    <a-switch v-model="formData.enabled" size="small">
                                        <span slot="checkedChildren">Aktif</span>
                                        <span slot="unCheckedChildren">Pasif</span>
                                    </a-switch>
                                </div>
                            </div>
                            <div class="visibility-section">
                                <label>Görünürlük</label>
                                <VisibilityChanger
                                    v-model="formData.visibility"
                                    :show-title="false"
                                    :alert-message="false"
                                    :hide-owner-section="true"
                                    :isChannel="false"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="form-actions">
                        <a-button size="large" @click="$router.push('/integrations/webtabs')"> İptal </a-button>
                        <a-button type="primary" size="large" html-type="submit" :loading="saving">
                            <span class="material-icons">{{ isEditMode ? "save" : "add" }}</span>
                            {{ isEditMode ? "Güncelle" : "Oluştur" }}
                        </a-button>
                    </div>
                </a-form>
            </a-spin>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import AppBreadCrumb from "../../../components/common/AppBreadCrumb.vue";
import MaterialIconPicker from "../Module/MaterialIconPicker.vue";
import VisibilityChanger from "../WhatsappV1/VisibilityChanger.vue";

export default {
    name: "WebTabDetail",
    components: {
        AppBreadCrumb,
        MaterialIconPicker,
        VisibilityChanger,
    },
    data() {
        return {
            form: this.$form.createForm(this),
            loading: false,
            saving: false,
            showQueryParams: false,
            showAdvanced: false,
            formData: {
                name: "",
                description: "",
                icon: "tab",
                iconColor: "#6366f1",
                type: "url",
                url: "",
                appId: null,
                queryParams: [],
                visibility: {
                    users: [],
                    departments: [],
                    owners: [],
                },
                order: 0,
                enabled: true,
                placement: "menu",
                toolbarMode: "popup",
                keepAlive: false,
                tableRecordConfig: {
                    scope: "all",
                    tableId: null,
                },
            },
        };
    },
    computed: {
        ...mapState({
            tables: (state) => state.table.tables,
        }),
        isEditMode() {
            return !!this.$route.params.id;
        },
        availableWebApps() {
            return this.$store.state.webapps.webApps.filter((app) => app.enabled && !app.isDeleted);
        },
        selectedApp() {
            if (!this.formData.appId) return null;
            return this.availableWebApps.find((app) => app._id === this.formData.appId);
        },
        isContactPlacement() {
            return ["contact-widget", "visitor-detail"].includes(this.formData.placement);
        },
        isTableRecordPlacement() {
            return this.formData.placement === "table-record-tab";
        },
        isTaskDetailPlacement() {
            return this.formData.placement === "task-detail-tab";
        },
        selectedTable() {
            if (!this.formData.tableRecordConfig?.tableId) return null;
            return this.tables.find((t) => t._id === this.formData.tableRecordConfig.tableId);
        },
        selectedTableFields() {
            if (!this.selectedTable) return [];
            return this.selectedTable.fields || [];
        },
        placementOptions() {
            return [
                {
                    value: "menu",
                    title: "Ana Menü",
                    description: "Sol sidebar'da göster",
                    longDescription:
                        "Web sekmeniz sol taraftaki ana menüde bir öğe olarak görünür. Tıklandığında tam sayfa olarak açılır.",
                    icon: "menu",
                    color: "linear-gradient(135deg, #10b981, #059669)",
                    previewImage: "https://static.supsis.live/common/image/webtab-menu.png",
                },
                {
                    value: "toolbar",
                    title: "Toolbar",
                    description: "Üst menüde buton",
                    longDescription:
                        "Üst araç çubuğunda bir buton olarak görünür. Popup olarak açılabilir veya tam sayfaya yönlendirebilir.",
                    icon: "web_asset",
                    color: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                    previewImage: "https://static.supsis.live/common/image/webtab-toolbar.png",
                },
                {
                    value: "contact-widget",
                    title: "Chat Widget",
                    description: "Sohbet panelinde",
                    longDescription:
                        "Chat penceresindeki kişi bilgileri panelinde bir widget olarak görünür. Kişi verilerine erişebilir.",
                    icon: "contact_page",
                    color: "linear-gradient(135deg, #f59e0b, #d97706)",
                    previewImage: "https://static.supsis.live/common/image/webtab-contact-widget.png",
                },
                {
                    value: "visitor-detail",
                    title: "Kişi Detay",
                    description: "Kişi sayfasında sekme",
                    longDescription:
                        "Kişi detay sayfasında ayrı bir sekme olarak görünür. Tüm kişi bilgilerine erişebilir.",
                    icon: "person_search",
                    color: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                    previewImage: "https://static.supsis.live/common/image/webtab-contact-tab.png",
                },
                {
                    value: "app-switcher",
                    title: "App Switcher",
                    description: "Uygulama değiştiricide",
                    longDescription:
                        "Sol menüdeki uygulama değiştirici panelinde görünür. Hızlı erişim için idealdir.",
                    icon: "apps",
                    color: "linear-gradient(135deg, #ec4899, #be185d)",
                    previewImage: "https://static.supsis.live/common/image/webtab-appswitcher.png",
                },
                {
                    value: "table-record-tab",
                    title: "Tablo Kayıt Sekmesi",
                    description: "Tablo kayıt detayında",
                    longDescription:
                        "Tablo kayıt detay sayfasında ayrı bir sekme olarak görünür. Kayıt verilerine erişebilir.",
                    icon: "table_view",
                    color: "linear-gradient(135deg, #14b8a6, #0d9488)",
                    previewImage: "https://static.supsis.live/common/image/webtab-tablerecord.png",
                },
                {
                    value: "task-detail-tab",
                    title: "Görev Detay Sekmesi",
                    description: "Görev detayında sekme",
                    longDescription:
                        "Görev detay penceresinde ayrı bir sekme olarak görünür. Görev verilerine erişebilir.",
                    icon: "task_alt",
                    color: "linear-gradient(135deg, #f97316, #ea580c)",
                    previewImage: "https://static.supsis.live/common/image/webtab-tasktab.png",
                },
            ];
        },
    },
    methods: {
        ...mapActions({
            getWebTab: "webtabs/getWebTab",
            createWebTab: "webtabs/createWebTab",
            updateWebTab: "webtabs/updateWebTab",
            fetchWebApps: "webapps/fetchWebApps",
        }),
        filterWebApp(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        filterTable(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        addQueryParam() {
            this.formData.queryParams.push({
                key: "",
                value: "",
                isDynamic: false,
                dynamicValue: "",
            });
        },
        removeQueryParam(index) {
            this.formData.queryParams.splice(index, 1);
        },
        handleDynamicValueChange(index, value) {
            if (value) {
                this.formData.queryParams[index].value = value;
                this.formData.queryParams[index].isDynamic = true;
            } else {
                this.formData.queryParams[index].isDynamic = false;
            }
        },
        async loadWebTab() {
            if (!this.isEditMode) return;

            this.loading = true;
            try {
                const webTab = await this.getWebTab(this.$route.params.id);
                this.formData = {
                    ...webTab,
                    queryParams: webTab.queryParams || [],
                    visibility: webTab.visibility || { users: [], departments: [], owners: [] },
                    tableRecordConfig: webTab.tableRecordConfig || { scope: "all", tableId: null },
                };

                this.formData.queryParams = this.formData.queryParams.map((param) => ({
                    ...param,
                    dynamicValue: param.isDynamic ? param.value : "",
                }));

                // Eğer query params varsa section'ı aç
                if (this.formData.queryParams.length > 0) {
                    this.showQueryParams = true;
                }
            } catch (error) {
                this.$message.error("Web sekmesi yüklenirken bir hata oluştu");
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        handleFromApp() {
            const fromAppId = this.$route.query.fromApp;
            if (!fromAppId || this.isEditMode) return;

            const app = this.availableWebApps.find((a) => a._id === fromAppId);
            if (!app) {
                console.warn("App not found for fromApp:", fromAppId);
                return;
            }

            // Form verilerini app'ten doldur
            this.formData.type = "app";
            this.formData.appId = app._id;
            this.formData.placement = "menu";
            this.formData.name = app.name;
            this.formData.description = app.description || "";
            this.formData.icon = app.icon || "web";
            this.formData.iconColor = app.iconColor || "#6366f1";

            // Default query params varsa kopyala
            if (app.defaultQueryParams && app.defaultQueryParams.length > 0) {
                this.formData.queryParams = app.defaultQueryParams.map((param) => ({
                    key: param.key,
                    value: param.value,
                    isDynamic: param.isDynamic || false,
                    dynamicValue: param.isDynamic ? param.value : "",
                }));
                this.showQueryParams = true;
            }
        },
        async handleCloneFrom() {
            const cloneFromId = this.$route.query.cloneFrom;
            if (!cloneFromId || this.isEditMode) return;

            this.loading = true;
            try {
                const webTab = await this.getWebTab(cloneFromId);
                if (!webTab) {
                    console.warn("WebTab not found for cloneFrom:", cloneFromId);
                    return;
                }

                // Form verilerini mevcut tab'dan kopyala (id hariç)
                this.formData = {
                    name: webTab.name + " (Kopya)",
                    description: webTab.description || "",
                    icon: webTab.icon || "tab",
                    iconColor: webTab.iconColor || "#6366f1",
                    type: webTab.type || "url",
                    url: webTab.url || "",
                    appId: webTab.appId || null,
                    queryParams: (webTab.queryParams || []).map((param) => ({
                        key: param.key,
                        value: param.value,
                        isDynamic: param.isDynamic || false,
                        dynamicValue: param.isDynamic ? param.value : "",
                    })),
                    visibility: {
                        users: webTab.visibility?.users || [],
                        departments: webTab.visibility?.departments || [],
                        owners: webTab.visibility?.owners || [],
                    },
                    order: (webTab.order || 0) + 1,
                    enabled: true,
                    placement: webTab.placement || "menu",
                    toolbarMode: webTab.toolbarMode || "popup",
                    keepAlive: webTab.keepAlive || false,
                    tableRecordConfig: {
                        scope: webTab.tableRecordConfig?.scope || "all",
                        tableId: webTab.tableRecordConfig?.tableId || null,
                    },
                };

                if (this.formData.queryParams.length > 0) {
                    this.showQueryParams = true;
                }
            } catch (error) {
                this.$message.error("Klonlanacak web sekmesi yüklenirken bir hata oluştu");
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        async handleSubmit() {
            if (!this.formData.name || !this.formData.icon) {
                this.$message.error("Lütfen zorunlu alanları doldurun");
                return;
            }

            if (this.formData.type === "url" && !this.formData.url) {
                this.$message.error("Lütfen URL girin");
                return;
            }

            if (this.formData.type === "app" && !this.formData.appId) {
                this.$message.error("Lütfen bir Web App seçin");
                return;
            }

            this.saving = true;
            try {
                const cleanedQueryParams = this.formData.queryParams.map((param) => ({
                    key: param.key,
                    value: param.value,
                    isDynamic: param.isDynamic || false,
                }));

                const data = {
                    ...this.formData,
                    queryParams: cleanedQueryParams,
                };

                if (this.isEditMode) {
                    await this.updateWebTab({ id: this.$route.params.id, data });
                    this.$message.success("Web sekmesi güncellendi");
                } else {
                    await this.createWebTab(data);
                    this.$message.success("Web sekmesi oluşturuldu");
                }

                this.$router.push("/integrations/webtabs");
            } catch (error) {
                this.$message.error("İşlem sırasında bir hata oluştu");
                console.error(error);
            } finally {
                this.saving = false;
            }
        },
    },
    mounted() {
        this.loadWebTab();
        this.fetchWebApps()
            .then(() => {
                this.handleFromApp();
            })
            .catch((err) => {
                console.error("Failed to fetch web apps:", err);
            });
        this.handleCloneFrom();
    },
};
</script>

<style lang="scss">
.placement-popover {
    .popover-image {
        width: 100%;
        max-width: 400px;
        border-radius: 8px;
        margin-bottom: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .popover-title {
        font-size: 15px;
        font-weight: 600;
        color: #1e293b;
        margin-bottom: 4px;
    }

    .popover-desc {
        font-size: 13px;
        color: #64748b;
        line-height: 1.5;
        max-width: 400px;
    }
}
.web-tab-detail-page {
    max-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    overflow-y: auto;

    .form-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 24px;
    }

    .form-header {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 32px;
        background: white;
        padding: 24px;
        border-radius: 16px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

        .form-header-icon {
            width: 56px;
            height: 56px;
            border-radius: 14px;
            background: linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%);
            display: flex;
            align-items: center;
            justify-content: center;

            .material-icons {
                font-size: 28px;
            }
        }

        .form-header-text {
            h1 {
                font-size: 24px;
                font-weight: 700;
                color: #1e293b;
                margin: 0 0 4px 0;
            }

            p {
                font-size: 14px;
                color: #64748b;
                margin: 0;
            }
        }
    }

    .webtab-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .form-section {
        background: white;
        border-radius: 16px;
        padding: 24px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

        &.collapsible {
            .section-content {
                margin-top: 20px;
                padding-top: 20px;
                border-top: 1px solid #e2e8f0;
            }

            &:not(.expanded) {
                padding-bottom: 20px;
            }
        }

        .section-header {
            display: flex;
            align-items: flex-start;
            gap: 16px;

            &.clickable {
                cursor: pointer;
                user-select: none;

                &:hover {
                    .section-number {
                        transform: scale(1.05);
                    }
                }
            }

            .section-number {
                width: 32px;
                height: 32px;
                min-width: 32px;
                border-radius: 10px;
                background: linear-gradient(135deg, #6366f1, #4f46e5);
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 700;
                font-size: 14px;
                transition: transform 0.2s;
            }

            .section-title {
                flex: 1;

                h3 {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 2px 0;
                }

                p {
                    font-size: 13px;
                    color: #64748b;
                    margin: 0;
                }
            }

            .expand-icon {
                color: #94a3b8;
                font-size: 24px;
            }
        }
    }

    // Placement Grid
    .placement-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
        margin-top: 20px;
    }

    .placement-card {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px;
        border: 2px solid #e2e8f0;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.2s;
        position: relative;

        &:hover {
            border-color: #cbd5e1;
            background: #f8fafc;
        }

        &.selected {
            border-color: #6366f1;
            background: #f0f4ff;

            .placement-card-icon {
                transform: scale(1.05);
            }
        }

        .placement-card-icon {
            width: 40px;
            height: 40px;
            min-width: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.2s;

            .material-icons {
                color: white;
                font-size: 20px;
            }
        }

        .placement-card-content {
            flex: 1;
            min-width: 0;

            .placement-card-title {
                font-size: 14px;
                font-weight: 600;
                color: #1e293b;
            }

            .placement-card-desc {
                font-size: 12px;
                color: #64748b;
            }
        }

        .placement-card-check {
            position: absolute;
            top: 8px;
            right: 8px;

            .material-icons {
                color: #6366f1;
                font-size: 20px;
            }
        }
    }

    .sub-options {
        margin-top: 16px;
        padding: 16px;
        background: #f8fafc;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        gap: 12px;

        .sub-option-row {
            display: flex;
            align-items: center;
            justify-content: space-between;

            label {
                font-size: 13px;
                color: #475569;

                .label-hint {
                    color: #94a3b8;
                    font-size: 11px;
                    margin-left: 4px;
                }
            }
        }
    }

    .info-badge {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 16px;
        padding: 12px 16px;
        background: #eff6ff;
        border-radius: 10px;
        color: #1d4ed8;
        font-size: 13px;

        .material-icons {
            font-size: 18px;
        }
    }

    // Type Options
    .type-options {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;
        margin-top: 20px;
    }

    .type-card {
        padding: 20px;
        border: 2px solid #e2e8f0;
        border-radius: 14px;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            border-color: #cbd5e1;
            background: #f8fafc;
        }

        &.selected {
            border-color: #6366f1;
            background: #f0f4ff;
        }

        .type-card-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 12px;
        }

        .type-card-icon {
            width: 44px;
            height: 44px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;

            &.url-icon {
                background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            }

            &.app-icon {
                background: linear-gradient(135deg, #8b5cf6, #6d28d9);
            }

            .material-icons {
                color: white;
                font-size: 22px;
            }
        }

        .type-card-check {
            .material-icons {
                color: #6366f1;
                font-size: 22px;
            }
        }

        h4 {
            font-size: 16px;
            font-weight: 600;
            color: #1e293b;
            margin: 0 0 8px 0;
        }

        p {
            font-size: 13px;
            color: #64748b;
            line-height: 1.5;
            margin: 0 0 12px 0;
        }

        .type-features {
            list-style: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 6px;

            li {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                color: #059669;

                .material-icons {
                    font-size: 14px;
                }
            }
        }
    }

    .type-config {
        margin-top: 16px;

        .app-select-wrapper {
            display: flex;
            gap: 12px;
        }

        .create-app-btn {
            display: flex;
            align-items: center;
            gap: 6px;
            white-space: nowrap;

            .material-icons {
                font-size: 18px;
            }
        }

        .selected-app-info {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 12px;
            padding: 10px 14px;
            background: #f0fdf4;
            border-radius: 8px;
            color: #059669;
            font-size: 13px;

            .material-icons {
                font-size: 16px;
            }
        }
    }

    .app-option {
        display: flex;
        align-items: center;
        gap: 8px;

        .material-icons {
            font-size: 18px;
        }

        .app-slug {
            margin-left: auto;
            color: #94a3b8;
            font-size: 12px;
        }
    }

    // Appearance
    .appearance-grid {
        display: flex;
        gap: 24px;
        margin-top: 20px;

        .appearance-icon-color {
            display: flex;
            gap: 16px;

            .icon-picker-compact,
            .color-picker-compact {
                label {
                    display: block;
                    font-size: 12px;
                    font-weight: 500;
                    color: #64748b;
                    margin-bottom: 6px;
                }
            }

            .color-input-row {
                display: flex;
                align-items: center;
                gap: 8px;
            }
        }

        .appearance-text {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 12px;

            .input-group {
                label {
                    display: block;
                    font-size: 12px;
                    font-weight: 500;
                    color: #64748b;
                    margin-bottom: 6px;

                    .required {
                        color: #ef4444;
                    }

                    .optional {
                        color: #94a3b8;
                        font-weight: 400;
                    }
                }
            }
        }
    }

    // Query Params
    .query-params-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .query-param-row {
        display: flex;
        align-items: center;
        gap: 8px;

        .param-equals {
            color: #94a3b8;
            font-weight: 500;
        }

        .remove-param-btn {
            padding: 0;
            height: auto;
            color: #94a3b8;

            &:hover {
                color: #ef4444;
            }

            .material-icons {
                font-size: 18px;
            }
        }
    }

    .add-param-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        margin-top: 12px;

        .material-icons {
            font-size: 16px;
        }
    }

    // Advanced
    .advanced-grid {
        display: flex;
        gap: 32px;
        margin-bottom: 20px;

        .advanced-item {
            display: flex;
            align-items: center;
            gap: 12px;

            label {
                font-size: 13px;
                color: #475569;
            }

            .hint {
                font-size: 11px;
                color: #94a3b8;
            }
        }
    }

    .visibility-section {
        label {
            display: block;
            font-size: 13px;
            font-weight: 500;
            color: #475569;
            margin-bottom: 12px;
        }
    }

    // Actions
    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 8px;

        .ant-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            height: 44px;
            padding: 0 24px;
            border-radius: 10px;
            font-weight: 500;

            .material-icons {
                font-size: 20px;
            }
        }
    }
}

@media (max-width: 768px) {
    .web-tab-detail-page {
        .form-container {
            padding: 16px;
        }

        .placement-grid,
        .type-options {
            grid-template-columns: 1fr;
        }

        .appearance-grid {
            flex-direction: column;
        }

        .type-config .app-select-wrapper {
            flex-direction: column;
        }
    }
}
</style>
